/**
 * Reiter „Live": Publivos Admin-Datenzentrum in der Leitwarte gespiegelt und
 * daneben die Live-Besucher der Kundenwebsites.
 *
 * Zwei Quellen, beide alle 15 Sekunden neu: /api/leitwarte/live (Publivo,
 * Vertrag PublivoLive) und /api/analytics/live (eigenes Tracking, Vertrag
 * LiveDaten). Fällt eine aus, bleibt die andere stehen; die Seite selbst
 * bricht nie. Ist Publivo nicht konfiguriert, kommt nur { verfuegbar: false }
 * zurück, deshalb werden alle Publivo-Felder mit Leerwerten aufgefüllt.
 *
 * Aufbau wie in Publivo (AdminOpsPage): Kennzahlen oben, wer online ist,
 * Aktivitäts-Feed, Pipeline, Plattformen, Fehler. Ohne Netzwerk-Karte, dafür
 * in den Leitwarte-Farben.
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPublivoLive, zahl, type PublivoLive } from '@/lib/leitwarte-api';
import { getLive, type LiveBesucher } from '@/lib/analytics-api';
import { KANAL_LABEL, abschnittText, aktionText, istKontakt } from '@/components/live/bausteine';
import { Karte, Kopf, Punkt, Legende, Laden, Leer, T, kapital, summe, mehrzahl, relativZeit } from './bausteine';
import { Sparkline, Saeulen, Balkenzeile, Segmente, RAMPE } from './charts';

// ── Übersetzungen ────────────────────────────────────────────────────────────

const PLATTFORM: Record<string, string> = {
  facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', google_business: 'Google Business',
  blog: 'Blog', tiktok: 'TikTok', threads: 'Threads', youtube: 'YouTube',
};
const plattformName = (l: string) => PLATTFORM[l.toLowerCase()] ?? kapital(l.replace(/[_-]+/g, ' '));

const GERAET: Record<string, string> = { mobile: 'Handy', smartphone: 'Handy', phone: 'Handy', desktop: 'Desktop', tablet: 'Tablet' };
const geraetName = (l: string) => GERAET[l.toLowerCase()] ?? kapital(l);

/** Aktion aus dem Publivo-Activity-Log als deutsches Verb; Reihenfolge zählt (generate vor create). */
const AKTION_VERB: Array<[RegExp, string]> = [
  [/publish/i, 'veröffentlicht'],
  [/generat/i, 'generiert'],
  [/schedul/i, 'eingeplant'],
  [/approv/i, 'freigegeben'],
  [/reject/i, 'abgelehnt'],
  [/archiv/i, 'archiviert'],
  [/creat|add/i, 'angelegt'],
  [/updat|edit|chang/i, 'bearbeitet'],
  [/delet|remov/i, 'gelöscht'],
  [/upload/i, 'hochgeladen'],
  [/login|sign.?in/i, 'angemeldet'],
  [/fail|error/i, 'fehlgeschlagen'],
];
const TYP_WORT: Record<string, string> = {
  content_piece: 'Beitrag', piece: 'Beitrag', post: 'Beitrag', content_variant: 'Variante', variant: 'Variante',
  content_input: 'Idee', input: 'Idee', suggestion: 'Vorschlag', idea: 'Idee', media: 'Medium', media_asset: 'Medium',
  image: 'Bild', video: 'Video', reel: 'Reel', blog_post: 'Blog-Artikel', blog: 'Blog-Artikel', pillar: 'Themensäule',
  content_pillar: 'Themensäule', organization: 'Kunde', user: 'Nutzer', chat: 'Chat', chat_project: 'Chat-Projekt',
  platform: 'Plattform', platform_connection: 'Plattform', calendar: 'Kalender', settings: 'Einstellungen',
};

function aktionLabel(a: PublivoLive['activity'][number]): string {
  const verb = AKTION_VERB.find(([re]) => re.test(a.aktion))?.[1];
  const typ = a.typ ? (TYP_WORT[a.typ.toLowerCase()] ?? kapital(a.typ.replace(/[_-]+/g, ' '))) : null;
  if (verb && typ) return `${typ} ${verb}`;
  if (verb) return kapital(verb);
  const roh = a.aktion.replace(/[_.:-]+/g, ' ').trim();
  return typ ? `${typ}: ${roh}` : roh;
}

/** Titel oder Name aus den Metadaten, falls der Log einen mitgibt. */
function metaTitel(meta: Record<string, unknown>): string | null {
  for (const k of ['title', 'titel', 'name', 'headline', 'topic']) {
    const v = meta[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

const LEER_PUBLIVO: PublivoLive = {
  verfuegbar: false,
  jetzt: '',
  online: [],
  activity: [],
  stunden: [],
  pipeline: { entwurf: 0, generiert: 0, review: 0, freigegeben: 0, geplant: 0, heute: 0, gesamt: 0 },
  plattformen: [],
  fehler: [],
  orgs: { gesamt: 0, aktiv: 0, trial: 0 },
};

const MONO: CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 };
const ZWEI_ZEILEN: CSSProperties = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

// ── Bausteine ────────────────────────────────────────────────────────────────

function Puls({ label, wert, rechts, fuss }: { label: string; wert: ReactNode; rechts?: ReactNode; fuss?: ReactNode }) {
  return (
    <Karte eng>
      <span className="lw-12 lw-sek">{label}</span>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8, minHeight: 40 }}>
        <span style={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>{wert}</span>
        {rechts}
      </div>
      {fuss ? <span className="lw-11 lw-ged">{fuss}</span> : null}
    </Karte>
  );
}

function NichtAngebunden({ label }: { label: string }) {
  return (
    <Karte eng>
      <span className="lw-12 lw-sek">{label}</span>
      <span style={{ fontSize: 18, fontWeight: 700, color: T.ged, lineHeight: 1.2, minHeight: 40, display: 'flex', alignItems: 'flex-end' }}>Nicht angebunden</span>
      <span className="lw-11 lw-ged">Publivo nicht angebunden.</span>
    </Karte>
  );
}

function Quellenfehler({ quelle, error }: { quelle: string; error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <span className="lw-12 lw-ged" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ color: T.ernst, fontWeight: 800 }}>!</span>
      {quelle} antwortet nicht: {msg}
    </span>
  );
}

function OnlineZeile({ u, jetztMs }: { u: PublivoLive['online'][number]; jetztMs: number }) {
  return (
    <div className="lw-innen" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center' }}>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Punkt farbe={T.gut} />
          <span className="lw-fett" style={{ fontSize: 13 }}>{u.name}</span>
          {u.admin ? <span className="lw-chip" style={{ color: T.blau2, borderColor: T.blau }}>du</span> : null}
          {u.org ? <span className="lw-chip">{u.org}</span> : null}
        </div>
        <span className="lw-ged" style={{ ...MONO, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={u.pfad}>{u.pfad}</span>
      </div>
      <span className="lw-11 lw-ged" style={{ whiteSpace: 'nowrap' }}>{relativZeit(u.zuletzt, jetztMs)}</span>
    </div>
  );
}

function BesucherZeile({ b, jetztMs }: { b: LiveBesucher; jetztMs: number }) {
  const jetzt = b.jetzt;
  const chips: ReactNode[] = [];
  if (b.kanal) chips.push(<span key="k" className="lw-chip">{KANAL_LABEL[b.kanal] ?? b.kanal}</span>);
  if (b.device) chips.push(<span key="d" className="lw-chip lw-ged">{geraetName(b.device)}</span>);
  if (b.land) chips.push(<span key="l" className="lw-chip lw-ged">{b.land}</span>);
  b.aktionen.forEach((a, i) => {
    const kontakt = istKontakt(a);
    chips.push(
      <span key={`a${i}`} className="lw-chip" style={kontakt ? { color: T.text, borderColor: T.orange } : undefined} title={aktionText(a)}>
        {aktionText(a)}
      </span>,
    );
  });
  if (b.anfrage) chips.push(<span key="f" className="lw-chip" style={{ color: T.text, borderColor: T.aqua }}>Anfrage{b.anfrage.name ? ` von ${b.anfrage.name}` : ''}</span>);

  return (
    <div className="lw-innen" style={{ display: 'grid', gridTemplateColumns: '8px minmax(0, 1fr) auto', gap: 10, alignItems: 'start' }}>
      <span style={{ paddingTop: 5 }}><Punkt farbe={b.online ? T.gut : T.rand2} /></span>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
          <span className="lw-fett" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{b.site_name}</span>
          <span className="lw-ged" style={{ ...MONO, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={jetzt?.pfad ?? b.pfad ?? undefined}>
            {jetzt?.pfad ?? b.pfad ?? ''}
          </span>
        </div>
        {chips.length ? <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>{chips}</div> : null}
        {jetzt?.abschnitt ? (
          <span className="lw-11 lw-sek">
            liest „{abschnittText(jetzt.abschnitt)}"{jetzt.scroll != null ? `, ${Math.round(jetzt.scroll)} % gescrollt` : ''}
            {jetzt.formular ? ', Formular offen' : ''}
          </span>
        ) : b.seiten > 1 ? (
          <span className="lw-11 lw-ged">{zahl(b.seiten)} Seiten{b.scroll_max != null ? `, bis ${Math.round(b.scroll_max)} % gescrollt` : ''}</span>
        ) : null}
      </div>
      <span className="lw-11 lw-ged" style={{ whiteSpace: 'nowrap', textAlign: 'right' }} title={`seit ${relativZeit(b.seit, jetztMs)}`}>
        {relativZeit(b.bewegt || b.zuletzt, jetztMs)}
      </span>
    </div>
  );
}

// ── Seite ────────────────────────────────────────────────────────────────────

const LISTE: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' };

export default function Live() {
  const publivo = useQuery({ queryKey: ['leitwarte', 'live', 'publivo'], queryFn: getPublivoLive, refetchInterval: 15_000, retry: 1 });
  const web = useQuery({ queryKey: ['leitwarte', 'live', 'web'], queryFn: () => getLive(30), refetchInterval: 15_000, retry: 1 });

  // Sekundentakt, damit „vor N s" mitläuft, ohne neu zu laden.
  const [jetztMs, setJetztMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setJetztMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!publivo.data && !web.data && (publivo.isLoading || web.isLoading)) return <Laden text="Lade die Live-Lage …" />;

  const pl: PublivoLive = { ...LEER_PUBLIVO, ...(publivo.data ?? {}) };
  const angebunden = pl.verfuegbar;
  const w = web.data;

  const stand = Math.max(publivo.dataUpdatedAt, web.dataUpdatedAt);
  const standText = stand > 0 ? `aktualisiert ${relativZeit(new Date(stand).toISOString(), jetztMs)}` : 'wird geladen …';

  // Kennzahlen
  const online = pl.online;
  const stunden = pl.stunden.length === 24 ? pl.stunden : [...pl.stunden, ...new Array(Math.max(0, 24 - pl.stunden.length)).fill(0)].slice(0, 24);
  const aktivitaeten24 = summe(stunden);
  const pipe = pl.pipeline;
  const fehler = pl.fehler;
  const letzterFehler = fehler[0];

  // Websites
  const besucher = [...(w?.besucher ?? [])]
    .sort((a, b) => Number(b.online) - Number(a.online) || Date.parse(b.bewegt || b.zuletzt) - Date.parse(a.bewegt || a.zuletzt))
    .slice(0, 12);
  const sitesOnline = [...(w?.sites ?? [])].filter((s) => s.online > 0).sort((a, b) => b.online - a.online);
  const takt = w?.takt ?? [];

  // Pipeline
  const stufen = [
    { label: 'Entwürfe', wert: pipe.entwurf, farbe: RAMPE[3] },
    { label: 'Generierung', wert: pipe.generiert, farbe: RAMPE[5] },
    { label: 'Zur Freigabe', wert: pipe.review, farbe: T.warn },
    { label: 'Freigegeben', wert: pipe.freigegeben, farbe: T.aqua },
    { label: 'Geplant', wert: pipe.geplant, farbe: T.blau },
  ];
  const offen = summe(stufen.map((s) => s.wert));
  const plattformen = [...pl.plattformen].sort((a, b) => b.wert - a.wert).slice(0, 7);

  return (
    <>
      <div className="lw-kopf" style={{ gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Live</span>
        <span className="lw-sek">Publivo-Datenzentrum und die Kundenwebsites, alle 15 Sekunden neu</span>
        <span className="lw-12 lw-ged" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Punkt farbe={publivo.isError && web.isError ? T.krit : publivo.isError || web.isError ? T.warn : T.gut} />
          {standText}
        </span>
      </div>
      {publivo.isError || web.isError ? (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: -8 }}>
          {publivo.isError ? <Quellenfehler quelle="Publivo" error={publivo.error} /> : null}
          {web.isError ? <Quellenfehler quelle="Website-Tracking" error={web.error} /> : null}
        </div>
      ) : null}

      {/* Kennzahlen */}
      <div className="lw-grid lw-g-puls">
        {angebunden ? (
          <Puls
            label="Gerade in Publivo"
            wert={<span style={{ color: online.length > 0 ? T.gut : T.text }}>{zahl(online.length)}</span>}
            rechts={online.length > 0 ? <span className="lw-12 lw-sek" style={{ paddingBottom: 3, textAlign: 'right', lineHeight: 1.3 }}>{zahl(online.filter((u) => !u.admin).length)} Kunden<br />{zahl(online.filter((u) => u.admin).length)} {mehrzahl(online.filter((u) => u.admin).length, 'Admin', 'Admins')}</span> : null}
            fuss={`${zahl(pl.orgs.aktiv)} zahlende Kunden, ${zahl(pl.orgs.trial)} in der Testphase, ${zahl(pl.orgs.gesamt)} gesamt`}
          />
        ) : (
          <NichtAngebunden label="Gerade in Publivo" />
        )}
        {angebunden ? (
          <Puls
            label="Aktivitäten 24 h"
            wert={zahl(aktivitaeten24)}
            rechts={<Saeulen werte={stunden} breite={120} hoehe={40} luecke={2} radius={1} titel={(i, v) => `${i === 23 ? 'letzte Stunde' : `vor ${24 - i} Std`}: ${zahl(v)}`} label="Aktivitäten je Stunde" />}
            fuss={`${zahl(stunden[23] ?? 0)} in der letzten Stunde`}
          />
        ) : (
          <NichtAngebunden label="Aktivitäten 24 h" />
        )}
        {angebunden ? (
          <Puls
            label="Pipeline"
            wert={<>{zahl(pipe.review)} <span className="lw-ged" style={{ fontWeight: 500, fontSize: 22 }}>·</span> {zahl(pipe.geplant)} <span className="lw-ged" style={{ fontWeight: 500, fontSize: 22 }}>·</span> {zahl(pipe.heute)}</>}
            fuss="zur Freigabe · geplant · heute live"
          />
        ) : (
          <NichtAngebunden label="Pipeline" />
        )}
        {angebunden ? (
          <Puls
            label="Fehler 24 h"
            wert={<span style={{ color: fehler.length > 0 ? T.krit : T.text }}>{zahl(fehler.length)}</span>}
            fuss={letzterFehler ? `zuletzt ${relativZeit(letzterFehler.at, jetztMs)}${letzterFehler.org ? `, ${letzterFehler.org}` : ''}` : 'keine Systemmeldungen'}
          />
        ) : (
          <NichtAngebunden label="Fehler 24 h" />
        )}
        <Puls
          label="Auf den Websites"
          wert={<span style={{ color: (w?.online ?? 0) > 0 ? T.gut : T.text }}>{zahl(w?.online ?? 0)}</span>}
          rechts={takt.length > 1 ? <Sparkline werte={takt} breite={120} hoehe={40} punkte="ende" label="Seitenaufrufe je Minute" /> : null}
          fuss={w ? `${zahl(w.aktiv)} aktiv, ${zahl(w.seitenaufrufe)} Seitenaufrufe in ${zahl(w.fenster_minuten)} Min` : web.isError ? 'Tracking antwortet nicht' : 'Lade …'}
        />
      </div>

      {/* Wer ist wo */}
      <div className="lw-grid lw-g-zwei" style={{ gap: 16 }}>
        <Karte style={{ gap: 10 }}>
          <Kopf titel="Wer ist gerade online" sub="in Publivo, Lebenszeichen der letzten 3 Minuten" rechts={angebunden && online.length ? <span className="lw-12 lw-sek">{zahl(online.length)} online</span> : null} />
          {!angebunden ? (
            <Leer text="Publivo nicht angebunden." />
          ) : online.length ? (
            <div style={LISTE}>
              {online.map((u) => <OnlineZeile key={u.id} u={u} jetztMs={jetztMs} />)}
            </div>
          ) : (
            <Leer text="Gerade niemand online." />
          )}
        </Karte>

        <Karte style={{ gap: 10 }}>
          <Kopf titel="Gerade auf den Kundenseiten" sub={`Besucher der letzten ${zahl(w?.fenster_minuten ?? 30)} Minuten, online zuerst`} rechts={w ? <span className="lw-12 lw-sek">{zahl(w.online)} online, {zahl(w.besucher.length)} im Fenster</span> : null} />
          {besucher.length ? (
            <div style={LISTE}>
              {besucher.map((b) => <BesucherZeile key={b.id} b={b} jetztMs={jetztMs} />)}
            </div>
          ) : (
            <Leer text={web.isError ? 'Das Tracking antwortet gerade nicht.' : 'Gerade niemand auf den Kundenseiten.'} />
          )}
          <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11 }}>
            {sitesOnline.length ? (
              <>
                <span className="lw-ged">Online je Seite:</span>
                {sitesOnline.map((s) => (
                  <span key={s.slug} className="lw-chip"><Punkt farbe={T.gut} groesse={6} />{s.name} {zahl(s.online)}</span>
                ))}
              </>
            ) : (
              <span className="lw-ged">Auf keiner Kundenseite ist gerade jemand.</span>
            )}
            {w?.gekappt ? <span className="lw-ged" style={{ marginLeft: 'auto' }}>Fenster gekappt, nicht alle Ereignisse geladen.</span> : null}
          </div>
        </Karte>
      </div>

      {/* Feed, Pipeline, Fehler */}
      <div className="lw-grid lw-g-drei">
        <Karte style={{ gap: 10 }}>
          <Kopf titel="Aktivitäts-Feed" sub="die letzten 25 Ereignisse in Publivo" />
          {!angebunden ? (
            <Leer text="Publivo nicht angebunden." />
          ) : pl.activity.length ? (
            <div style={{ ...LISTE, gap: 0 }}>
              {pl.activity.slice(0, 25).map((a, i) => {
                const titel = metaTitel(a.meta);
                return (
                  <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr)', gap: 8, alignItems: 'baseline', padding: '5px 0', borderTop: i ? `1px solid ${T.rand}` : undefined, fontSize: 12 }}>
                    <span className="lw-11 lw-ged" style={{ whiteSpace: 'nowrap' }}>{relativZeit(a.at, jetztMs)}</span>
                    <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span className="lw-sek" title={`${a.aktion}${a.typ ? ` · ${a.typ}` : ''}`}>{aktionLabel(a)}</span>
                      {titel ? <span className="lw-ged" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{titel}</span> : null}
                      {a.org ? <span className="lw-chip">{a.org}</span> : <span className="lw-chip lw-ged">System</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <Leer text="Noch keine Ereignisse." />
          )}
        </Karte>

        <Karte style={{ gap: 12 }}>
          <Kopf titel="Content-Pipeline" sub={angebunden ? `${zahl(offen)} ${mehrzahl(offen, 'Beitrag', 'Beiträge')} unterwegs` : undefined} />
          {!angebunden ? (
            <Leer text="Publivo nicht angebunden." />
          ) : (
            <>
              <Segmente teile={stufen} label="Content-Pipeline nach Status" />
              <Legende groesse={11} eintraege={stufen.map((s) => ({ farbe: s.farbe, label: <>{s.label} <span className="lw-weiss lw-fett">{zahl(s.wert)}</span></> }))} />
              <div className="lw-grid lw-g-zwei">
                <div className="lw-innen" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="lw-11 lw-ged">Heute live gegangen</span>
                  <span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, color: pipe.heute > 0 ? T.gut : T.text }}>{zahl(pipe.heute)}</span>
                </div>
                <div className="lw-innen" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="lw-11 lw-ged">Veröffentlicht gesamt</span>
                  <span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>{zahl(pipe.gesamt)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 'auto' }}>
                <span className="lw-12 lw-sek">Plattformen der Woche</span>
                {plattformen.length ? (
                  <Balkenzeile zeilen={plattformen.map((r) => ({ label: plattformName(r.label), wert: r.wert }))} farbe={T.orange} labelBreite={104} wertBreite={36} luecke={8} abstand={4} />
                ) : (
                  <Leer text="Keine Beiträge in 7 Tagen." />
                )}
              </div>
            </>
          )}
        </Karte>

        <Karte style={{ gap: 10 }}>
          <Kopf titel="Fehler 24 h" sub="Systemmeldungen aus Publivo" rechts={angebunden && fehler.length ? <span className="lw-12" style={{ color: T.krit, fontWeight: 700 }}>{zahl(fehler.length)}</span> : null} />
          {!angebunden ? (
            <Leer text="Publivo nicht angebunden." />
          ) : fehler.length ? (
            <div style={LISTE}>
              {fehler.map((f) => (
                <div key={f.id} className="lw-innen" style={{ display: 'grid', gridTemplateColumns: '8px minmax(0, 1fr)', gap: 10, alignItems: 'start' }}>
                  <span style={{ paddingTop: 5 }}><Punkt farbe={T.krit} /></span>
                  <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span className="lw-fett" style={{ fontSize: 13 }}>{f.titel || 'Systemmeldung'}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11 }}>
                      {f.org ? <span className="lw-chip">{f.org}</span> : null}
                      <span className="lw-ged">{relativZeit(f.at, jetztMs)}</span>
                    </div>
                    {f.text ? <span className="lw-12 lw-sek" style={ZWEI_ZEILEN} title={f.text}>{f.text}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Leer text="Keine Fehler in 24 Stunden." />
          )}
        </Karte>
      </div>
    </>
  );
}
